import type { ReactNode } from "react";
import Wordmark from "../Wordmark";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { cn } from "../ui/cn";

interface ScreenHeaderProps {
  /**
   * Languages the current quiz is renderable in. When supplied with 2+
   * entries the LanguageSwitcher dropdown filters to those codes.
   * Omit on pre-session screens (Home, Join before submitGetSession)
   * so the picker shows all 10 supported locales.
   */
  available?: string[];
  /**
   * Rightmost slot — typically a connection status pill, a question
   * counter, or a small button. Sits next to the LanguageSwitcher.
   */
  trailing?: ReactNode;
  /**
   * Match the existing Wordmark sizes across screens.
   * - sm  → in-session compact (Play screens)
   * - md  → default (Host screens)
   * - lg  → entry screens (Home)
   */
  wordmark?: "sm" | "md" | "lg";
  /**
   * Compact = short variant of the Wordmark (Play screens use the
   * dot mark only on mobile). Mirrors Wordmark's existing `short` prop.
   */
  short?: boolean;
  className?: string;
}

/**
 * The canonical screen header — Wordmark + LanguageSwitcher (+ optional
 * trailing slot). Used on every routed screen. Replaces ~10 inline
 * `<header>` patterns that had drifted into three subtly different
 * class signatures.
 */
export function ScreenHeader({
  available,
  trailing,
  wordmark = "md",
  short,
  className,
}: ScreenHeaderProps) {
  return (
    <header
      className={cn(
        "flex items-center justify-between mb-6 md:mb-8",
        className,
      )}
    >
      <Wordmark size={wordmark} short={short} />
      <div className="flex items-center gap-3">
        <LanguageSwitcher available={available} />
        {trailing}
      </div>
    </header>
  );
}
